import React from "react";
import type { z } from "zod/v4";
import { Flex } from "@/components/Flex/Flex";
import { Grid } from "@/components/Grid/Grid";
import { Icon } from "@/components/Icon";
import { Inline } from "@/components/Inline/Inline";
import { Section } from "@/components/Section";
import { Stack } from "@/components/Stack/Stack";
import { Text } from "@/components/Text";
import type { FooterPropsSchema } from "@/schemas/sections";
import type { SectionWrapperProps } from "@/types/section-wrapper";

export type FooterSectionProps = z.input<typeof FooterPropsSchema> & SectionWrapperProps;

export function FooterSection({
  company,
  links,
  legal,
  background = "base-100",
  padding,
  pattern,
  className,
  id,
}: FooterSectionProps) {
  const description = company?.description;

  return (
    <Section
      as="div"
      id={id}
      background={background}
      padding={padding}
      pattern={pattern}
      className={className}
      containerWidth="max-w-340"
    >
      <footer data-component="FooterSection">
        <div className="py-12">
          <Grid columns={4} gap={32} className="mb-8">
            {links?.columns?.map((column, index) => (
              <nav key={index} className="text-left">
                <Text variant="subheading" as="h6" className="mb-4">
                  {column.title}
                </Text>
                <Stack as="ul" gap={8}>
                  {column.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <Flex
                        as="a"
                        href={link.href}
                        inline
                        align="center"
                        gap={8}
                        className="opacity-70 transition-opacity hover:opacity-100"
                        title={link.text}
                      >
                        {link.icon && (
                          <Icon name={link.icon} size="md" className={link.iconColor || ""} />
                        )}
                        <Text variant="body-sm" as="span">
                          {link.text}
                        </Text>
                      </Flex>
                    </li>
                  ))}
                </Stack>
              </nav>
            ))}

            <div className="text-left">
              {company?.logo && <img src={company.logo} alt={company.name} className="mb-3 h-10" />}
              {company?.name && (
                <Text variant="heading" as="div" className="mb-2">
                  {company.name}
                </Text>
              )}
              {description?.length ? (
                <Text variant="body-sm" as="div" className="opacity-70">
                  {description.map((text, index) => (
                    <React.Fragment key={index}>
                      {text}
                      {index < description.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Text>
              ) : null}
            </div>
          </Grid>

          <Flex
            direction={{ base: "column", lg: "row" }}
            justify="between"
            gap={16}
            className="items-start lg:items-center"
          >
            {legal?.copyright && (
              <Text variant="caption" className="opacity-60">
                {legal.copyright}
              </Text>
            )}
            {legal?.links && legal.links.length > 0 && (
              <Inline as="nav" gap={24} align="stretch">
                {legal.links.map((link, index) => (
                  <a
                    key={index}
                    href={link.href}
                    className="opacity-70 transition-opacity hover:opacity-100"
                  >
                    <Text variant="caption" as="span">
                      {link.text}
                    </Text>
                  </a>
                ))}
              </Inline>
            )}
          </Flex>
        </div>
      </footer>
    </Section>
  );
}

FooterSection.displayName = "FooterSection";
